import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  SETTING_PAGES,
  SETTING_TYPES,
  useUserSettings,
} from '@/features/sliced-form/hooks/use-user-settings'
import { nowIso } from '@/utils/now-iso'
import { useMaxExpressions } from '@/context/plan-constraint-hooks'
import {
  generateLibraryExpressionId,
  LibraryExpressionSchema,
  type ExpressionNamespace,
  type LibraryExpression,
} from '@/features/sliced-form/schemas/expression'
import { buildLibrarySeedsForNamespace } from '../seeds'
import { openExpressionsBroadcast } from './expressions-broadcast'
import { EVENTS, trackEvent } from '@/services/analytics'
import { seededNamespaces } from './seeded-namespaces'

export {
  __resetExpressionSeedingForTest,
  __suppressExpressionSeedingForTest,
} from './seeded-namespaces'

/**
 * Thrown by library mutations when an action would exceed the user's plan
 * cap. UI should catch it and raise the shared upgrade pitch through
 * `openExpressionUpgradePrompt(err.cap)` rather than showing a generic error.
 * The pitch names the plan itself, so no caller should write one down.
 */
export class ExpressionLibraryCapError extends Error {
  cap: number
  constructor(cap: number) {
    super(`Expression library cap reached (${cap}).`)
    this.name = 'ExpressionLibraryCapError'
    this.cap = cap
  }
}

export type LibraryAction = {
  /** Add a new expression. Throws ExpressionLibraryCapError if over cap. */
  add: (
    body: Omit<LibraryExpression, 'id' | 'createdAt' | 'updatedAt' | 'namespace' | 'deletedAt'>,
  ) => Promise<LibraryExpression>
  /** Replace a live expression's body. No-op on an unknown or deleted id. */
  update: (
    id: string,
    patch: Partial<Omit<LibraryExpression, 'id' | 'namespace' | 'createdAt' | 'deletedAt'>>,
  ) => Promise<void>
  /**
   * Soft-delete: stamps `deletedAt`. The row stays in the record so every
   * reference keeps resolving; pickers hide it and the cap ignores it.
   */
  remove: (id: string) => Promise<void>
  /**
   * Clear `deletedAt` on a deleted row. Counts against the cap like `add`
   * and throws `ExpressionLibraryCapError` at the cap. No-op on a live id;
   * throws on an unknown id.
   */
  restore: (id: string) => Promise<LibraryExpression>
  /** Reorder the live rows by an explicit id list (a permutation of the live ids). */
  reorder: (orderedIds: string[]) => Promise<void>
  /**
   * Install seed expressions only if the record holds no rows at all,
   * deleted ones included. Returns the seeds installed. Idempotent —
   * concurrent calls are guarded by a ref so we don't double-seed.
   */
  seedIfEmpty: (seeds: LibraryExpression[]) => Promise<LibraryExpression[]>
}

export type UseExpressionLibraryResult = {
  /** Live rows in persisted order: what pickers offer. */
  expressions: LibraryExpression[]
  /** Deleted rows, for the manager's Restore list. */
  deletedExpressions: LibraryExpression[]
  /** Every row, live and deleted, keyed by id: what readers resolve against. */
  libraryById: Record<string, LibraryExpression>
  isLoading: boolean
  /** Hard limit per the user's current plan tier. */
  planCap: number
  /** True when the live count has reached the plan cap. */
  isAtCap: boolean
  /** Live rows beyond the cap (after a downgrade). Disabled in pickers but not deleted. */
  disabledIds: ReadonlySet<string>
} & LibraryAction

const settingTypeFor = (namespace: ExpressionNamespace): string =>
  namespace === 'realtime' ? SETTING_TYPES.EXPRESSION_REALTIME : SETTING_TYPES.EXPRESSION_HISTORICAL

export const isDeletedExpression = (row: LibraryExpression): boolean => row.deletedAt != null

/**
 * Read-only view of a namespace's library, live and deleted rows alike.
 * For readers that only resolve refs (the History page query) and must not
 * open a broadcast channel or seed.
 */
export function useExpressionLibraryRows(namespace: ExpressionNamespace): LibraryExpression[] {
  const persistence = useUserSettings<LibraryExpression>({
    page: SETTING_PAGES.EXPRESSIONS,
    type: settingTypeFor(namespace),
  })
  return useValidatedRows(persistence.setting, namespace)
}

/**
 * The same read-only view keyed by id, with the settings loading flag so a
 * request builder can hold a query that references an expression until the
 * library has arrived instead of dropping the ref as `library-missing`.
 */
export function useExpressionLibraryById(namespace: ExpressionNamespace): {
  libraryById: Record<string, LibraryExpression>
  isLoading: boolean
} {
  const persistence = useUserSettings<LibraryExpression>({
    page: SETTING_PAGES.EXPRESSIONS,
    type: settingTypeFor(namespace),
  })
  const rows = useValidatedRows(persistence.setting, namespace)
  const libraryById = useMemo(() => Object.fromEntries(rows.map((e) => [e.id, e])), [rows])
  return { libraryById, isLoading: persistence.isLoading }
}

/**
 * True once both libraries have settled (IndexedDB read done and the first API
 * request resolved or failed). The workspace holds its panels on this so no
 * request compiles an `expr_*` ref before the row it names can be found.
 */
export function useExpressionLibrariesReady(): boolean {
  const realtime = useUserSettings<LibraryExpression>({
    page: SETTING_PAGES.EXPRESSIONS,
    type: settingTypeFor('realtime'),
  })
  const historical = useUserSettings<LibraryExpression>({
    page: SETTING_PAGES.EXPRESSIONS,
    type: settingTypeFor('historical'),
  })
  return !realtime.isLoading && !historical.isLoading
}

function useValidatedRows(raw: unknown[], namespace: ExpressionNamespace): LibraryExpression[] {
  return useMemo<LibraryExpression[]>(() => {
    const out: LibraryExpression[] = []
    for (const row of raw) {
      const parsed = LibraryExpressionSchema.safeParse(row)
      if (parsed.success && parsed.data.namespace === namespace) {
        out.push(parsed.data)
      }
    }
    return out
  }, [raw, namespace])
}

function validRows(raw: unknown): LibraryExpression[] {
  if (!Array.isArray(raw)) return []
  const out: LibraryExpression[] = []
  for (const row of raw) {
    const parsed = LibraryExpressionSchema.safeParse(row)
    if (parsed.success) out.push(parsed.data)
  }
  return out
}

/**
 * Library data is shaped as `LibraryExpression[]` (one collection per
 * namespace) — order in the array is the canonical render order, mirroring
 * how `filterProfiles` and `columnProfiles` are persisted. Deleted rows
 * keep their place in the array; the live view is a filter over it.
 *
 * We validate items against the Zod schema after load and silently drop
 * malformed entries rather than crash the manager.
 */
export function useExpressionLibrary(namespace: ExpressionNamespace): UseExpressionLibraryResult {
  const type = settingTypeFor(namespace)
  const persistence = useUserSettings<LibraryExpression>({
    page: SETTING_PAGES.EXPRESSIONS,
    type,
  })

  const planCap = useMaxExpressions()
  const seedingRef = useRef(false)
  const broadcastRef = useRef<ReturnType<typeof openExpressionsBroadcast> | null>(null)

  // Another tab wrote the library: refetch so this tab resolves the new rows.
  useEffect(() => {
    const binding = openExpressionsBroadcast({
      namespace,
      onTemplatesUpdated: () => persistence.reload(),
    })
    broadcastRef.current = binding
    return () => {
      binding.close()
      broadcastRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, persistence.reload])

  const allExpressions = useValidatedRows(persistence.setting, namespace)
  const expressions = useMemo(
    () => allExpressions.filter((e) => !isDeletedExpression(e)),
    [allExpressions],
  )
  const deletedExpressions = useMemo(
    () => allExpressions.filter(isDeletedExpression),
    [allExpressions],
  )
  const libraryById = useMemo(
    () => Object.fromEntries(allExpressions.map((e) => [e.id, e])),
    [allExpressions],
  )

  // After a downgrade the user may have N > cap. Don't auto-prune — disable
  // the over-cap items in pickers but keep them on disk in case they upgrade
  // back. Order in the persisted array is treated as importance: the first
  // `cap` live items remain enabled.
  const disabledIds = useMemo<Set<string>>(() => {
    const overflow = new Set<string>()
    if (expressions.length <= planCap) return overflow
    for (let i = planCap; i < expressions.length; i++) {
      overflow.add(expressions[i]!.id)
    }
    return overflow
  }, [expressions, planCap])

  const isAtCap = expressions.length >= planCap

  // Read the persisted rows right before each write so concurrent writers
  // in this tab don't race on the render-time copy.
  const latestRows = useCallback(() => validRows(persistence.setting), [persistence.setting])
  const liveCount = (rows: LibraryExpression[]) =>
    rows.filter((r) => !isDeletedExpression(r)).length

  const write = useCallback(
    async (rows: LibraryExpression[]) => {
      await persistence.save(rows)
      broadcastRef.current?.post()
    },
    [persistence],
  )

  const add = useCallback<LibraryAction['add']>(
    async (body) => {
      const latest = latestRows()
      if (liveCount(latest) >= planCap) throw new ExpressionLibraryCapError(planCap)
      const ts = nowIso()
      const next: LibraryExpression = {
        ...body,
        id: generateLibraryExpressionId(),
        namespace,
        createdAt: ts,
        updatedAt: ts,
      }
      await write([...latest, next])
      return next
    },
    [latestRows, planCap, namespace, write],
  )

  const update = useCallback<LibraryAction['update']>(
    async (id, patch) => {
      const latest = latestRows()
      const idx = latest.findIndex((e) => e.id === id)
      if (idx === -1 || isDeletedExpression(latest[idx]!)) return
      const next = [...latest]
      next[idx] = { ...latest[idx]!, ...patch, updatedAt: nowIso() }
      await write(next)
      trackEvent(EVENTS.EXPRESSION_TEMPLATE_EDITED, { namespace })
    },
    [latestRows, namespace, write],
  )

  const remove = useCallback<LibraryAction['remove']>(
    async (id) => {
      const latest = latestRows()
      const idx = latest.findIndex((e) => e.id === id)
      if (idx === -1 || isDeletedExpression(latest[idx]!)) return
      const next = [...latest]
      next[idx] = { ...latest[idx]!, deletedAt: nowIso() }
      await write(next)
      trackEvent(EVENTS.EXPRESSION_TEMPLATE_DELETED, { namespace })
    },
    [latestRows, namespace, write],
  )

  const restore = useCallback<LibraryAction['restore']>(
    async (id) => {
      const latest = latestRows()
      const idx = latest.findIndex((e) => e.id === id)
      if (idx === -1) throw new Error(`restore: no library row at ${id}`)
      const row = latest[idx]!
      if (!isDeletedExpression(row)) return row
      if (liveCount(latest) >= planCap) throw new ExpressionLibraryCapError(planCap)
      const { deletedAt: _gone, ...rest } = row
      const restored: LibraryExpression = { ...rest, updatedAt: nowIso() }
      const next = [...latest]
      next[idx] = restored
      await write(next)
      trackEvent(EVENTS.EXPRESSION_TEMPLATE_RESTORED, { namespace })
      return restored
    },
    [latestRows, planCap, namespace, write],
  )

  const reorder = useCallback<LibraryAction['reorder']>(
    async (orderedIds) => {
      const latest = latestRows()
      const live = latest.filter((e) => !isDeletedExpression(e))
      if (orderedIds.length !== live.length) return
      const byId = new Map(live.map((e) => [e.id, e]))
      const next: LibraryExpression[] = []
      for (const id of orderedIds) {
        const e = byId.get(id)
        if (!e) return
        next.push(e)
      }
      await write([...next, ...latest.filter(isDeletedExpression)])
    },
    [latestRows, write],
  )

  const seedIfEmpty = useCallback<LibraryAction['seedIfEmpty']>(
    async (seeds) => {
      if (seedingRef.current) return []
      if (allExpressions.length > 0) return []
      if (seeds.length === 0) return []
      seedingRef.current = true
      try {
        const allowed = seeds.slice(0, planCap)
        await write(allowed)
        return allowed
      } finally {
        seedingRef.current = false
      }
    },
    [allExpressions.length, write, planCap],
  )

  // Auto-install default seeds when the library is confirmed loaded and
  // holds no rows at all. A user who deleted the seed keeps an empty picker
  // rather than getting the seed back. Guarded module-wide so multiple
  // panels racing to mount the same namespace can't double-install.
  useEffect(() => {
    if (persistence.isLoading) return
    if (allExpressions.length > 0) return
    if (seededNamespaces.has(namespace)) return
    seededNamespaces.add(namespace)
    const seeds = buildLibrarySeedsForNamespace(namespace, () => true)
    if (seeds.length === 0) return
    void seedIfEmpty(seeds).catch(() => {
      // Allow a retry on the next mount if persistence rejected (e.g. a
      // transient network error). The guard releasing here is intentional.
      seededNamespaces.delete(namespace)
    })
  }, [namespace, persistence.isLoading, allExpressions.length, seedIfEmpty])

  return {
    expressions,
    deletedExpressions,
    libraryById,
    isLoading: persistence.isLoading,
    planCap,
    isAtCap,
    disabledIds,
    add,
    update,
    remove,
    restore,
    reorder,
    seedIfEmpty,
  }
}
